// src/pages/AccountAdministration.tsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HighLogo from "../../components/HighLogo";
import logo from "../../../assets/logo.png";

type SubTopic = {
  id: string;
  title: string;
  body: string[];
};

type TopicGroup = {
  id: string;
  title: string;
  description?: string;
  subtopics: SubTopic[];
};

const AccountAdministration: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  const arr = (key: string, fallback: string[]): string[] => {
    const value = t(key, { returnObjects: true });
    return Array.isArray(value) ? (value as string[]) : fallback;
  };

  const groups: TopicGroup[] = useMemo(
    () => [
      {
        id: "account-overview",
        title: t("help.account.sidebar.accountOverview", "Account Overview"),
        description: t(
          "help.account.overview.description",
          "Learn how your account works, where to find key settings, and how to manage your profile safely."
        ),
        subtopics: [
          {
            id: "intro",
            title: t("help.account.topics.intro.title", "What is your account?"),
            body: arr("help.account.topics.intro.bullets", [
              "Your account gives you access to your profile, settings, privacy controls, saved activity, and platform features.",
              "Use this section to understand the basics before changing personal or security information.",
            ]),
          },
          {
            id: "profile-basics",
            title: t("help.account.subtopics.profileBasics", "Profile basics"),
            body: [
              "Your profile contains the public or semi-public information other users may see.",
              "Keep your name, avatar, biography, and contact details accurate and appropriate.",
              "Some profile information may be visible depending on your privacy settings.",
            ],
          },
          {
            id: "account-navigation",
            title: t("help.account.subtopics.accountNavigation", "Where to find account tools"),
            body: [
              "Open Settings from your profile menu.",
              "Use Account Settings for login, email, password, and connected account options.",
              "Use Privacy and Security for visibility, data, and protection controls.",
            ],
          },
        ],
      },
      {
        id: "sign-in-access",
        title: t("help.account.sidebar.signInAccess", "Sign in & Access"),
        subtopics: [
          {
            id: "registration",
            title: t("help.account.topics.registration.title", "Create an account"),
            body: arr("help.account.topics.registration.bullets", [
              "Choose a valid email address or supported sign-in method.",
              "Follow the verification steps if required.",
              "Complete your basic profile after registration.",
            ]),
          },
          {
            id: "login",
            title: t("help.account.topics.login.title", "Log in"),
            body: arr("help.account.topics.login.bullets", [
              "Enter your email, username, or supported login method.",
              "Check your password carefully.",
              "If login fails, reset your password or check whether your account is restricted.",
            ]),
          },
          {
            id: "forgot-password",
            title: t("help.account.topics.forgotPassword.title", "Forgot password"),
            body: arr("help.account.topics.forgotPassword.bullets", [
              "Use the Forgot Password option on the login page.",
              "Check your inbox and spam folder for the reset email.",
              "Create a strong new password that you do not use elsewhere.",
            ]),
          },
        ],
      },
      {
        id: "settings-preferences",
        title: t("help.account.sidebar.settingsPreferences", "Settings & Preferences"),
        subtopics: [
          {
            id: "settings",
            title: t("help.account.topics.settings.title", "General settings"),
            body: arr("help.account.topics.settings.bullets", [
              "Update language, notifications, display preferences, and basic account behavior.",
              "Review settings regularly to keep the platform comfortable for your use.",
            ]),
          },
          {
            id: "account-settings",
            title: t("help.account.topics.accountSettings.title", "Account settings"),
            body: arr("help.account.topics.accountSettings.bullets", [
              "Manage your email, password, username, and connected login methods.",
              "Some changes may require verification for security.",
            ]),
          },
          {
            id: "profile-management",
            title: t("help.account.topics.profileManagement.title", "Profile management"),
            body: arr("help.account.topics.profileManagement.bullets", [
              "Edit your display name, profile picture, biography, and visible profile information.",
              "Make sure your profile follows community and platform rules.",
            ]),
          },
        ],
      },
      {
        id: "privacy-security",
        title: t("help.account.sidebar.privacySecurity", "Privacy & Security"),
        subtopics: [
          {
            id: "data-privacy",
            title: t("help.account.topics.dataPrivacy.title", "Data and privacy"),
            body: arr("help.account.topics.dataPrivacy.bullets", [
              "Review what information is collected and how it may be used.",
              "Adjust visibility settings where available.",
              "Use privacy controls to limit unwanted exposure.",
            ]),
          },
          {
            id: "security",
            title: t("help.account.topics.security.title", "Security"),
            body: arr("help.account.topics.security.bullets", [
              "Use a strong password.",
              "Do not share login codes or reset links.",
              "Review suspicious activity and update your password if needed.",
            ]),
          },
          {
            id: "secure-account",
            title: t("help.account.subtopics.secureAccount", "Keep your account secure"),
            body: [
              "Avoid using the same password across multiple services.",
              "Log out from shared devices.",
              "Contact support immediately if you believe someone else accessed your account.",
            ],
          },
        ],
      },
      {
        id: "support",
        title: t("help.account.sidebar.support", "Help & Support"),
        subtopics: [
          {
            id: "help-support",
            title: t("help.account.topics.helpSupport.title", "Contact support"),
            body: arr("help.account.topics.helpSupport.bullets", [
              "Use Help and Support when you cannot solve an account problem yourself.",
              "Include your account email, a clear description, and screenshots if helpful.",
            ]),
          },
          {
            id: "report-problem",
            title: t("help.account.subtopics.reportProblem", "Report an account problem"),
            body: [
              "Describe what happened and when it started.",
              "Mention any error messages you saw.",
              "Do not include your password or private verification codes.",
            ],
          },
        ],
      },
    ],
    [t]
  );

  const allIds = useMemo(
    () => groups.flatMap((group) => group.subtopics.map((s) => s.id)),
    [groups]
  );

  const [activeAnchor, setActiveAnchor] = useState<string>("intro");
  const [openGroup, setOpenGroup] = useState<string>("account-overview");

  const scrollTo = (id: string) => {
    const container = document.getElementById("aa-scroll");
    const el = document.getElementById(id);
    if (!container || !el) return;

    const top =
      el.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      18;

    container.scrollTo({ top, behavior: "smooth" });
    window.history.replaceState(null, "", `#${id}`);
    setActiveAnchor(id);

    const parent = groups.find((g) => g.subtopics.some((s) => s.id === id));
    if (parent) setOpenGroup(parent.id);
  };

  useEffect(() => {
    const hash = (location.hash || "").replace("#", "").trim();
    if (!hash) return;

    requestAnimationFrame(() => scrollTo(hash));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const container = document.getElementById("aa-scroll");
    if (!container) return;

    const handler = () => {
      let current = allIds[0] || "intro";

      for (const id of allIds) {
        const el = document.getElementById(id);
        if (!el) continue;

        const elTop =
          el.getBoundingClientRect().top -
          container.getBoundingClientRect().top;

        if (elTop <= 130) current = id;
      }

      setActiveAnchor(current);

      const parent = groups.find((g) => g.subtopics.some((s) => s.id === current));
      if (parent) setOpenGroup(parent.id);
    };

    handler();
    container.addEventListener("scroll", handler, { passive: true });
    return () => container.removeEventListener("scroll", handler);
  }, [allIds, groups]);

  return (
    <div style={shell}>
      <header style={header}>
        <div style={headerLeft} onClick={() => navigate("/gallery")}>
          <HighLogo
            src={logo}
            style={{
              width: 100,
              display: "block",
              marginLeft: "-20px",
              marginTop: "6px",
            }}
            padding={0}
            onClick={() => navigate("/gallery")}
          />
        </div>

        <div style={headerTitle}>{t("help.account.pageTitle", "Account Administration")}</div>
      </header>

      <main style={main}>
        <aside style={sidebar}>
          <div style={sidebarHeader}>
            <div style={sidebarEyebrow}>{t("help.account.ui.helpCenter", "Help Center")}</div>
            <h2 style={sidebarTitle}>{t("help.account.pageTitle", "Account Administration")}</h2>
            <p style={sidebarText}>
              {t(
                "help.account.pageSubtitle",
                "Find help for account access, settings, privacy, security, and support."
              )}
            </p>
          </div>

          <nav style={nav}>
            {groups.map((group) => {
              const isOpen = openGroup === group.id;
              const groupActive = group.subtopics.some((s) => s.id === activeAnchor);

              return (
                <div key={group.id} style={navGroup}>
                  <button
                    type="button"
                    onClick={() => setOpenGroup(isOpen ? "" : group.id)}
                    style={{
                      ...groupBtn,
                      ...(groupActive ? groupBtnActive : null),
                    }}
                  >
                    <span>{group.title}</span>
                    <span style={chevron}>{isOpen ? "−" : "+"}</span>
                  </button>

                  {isOpen && (
                    <div style={subNav}>
                      {group.subtopics.map((sub) => (
                        <button
                          key={sub.id}
                          type="button"
                          onClick={() => scrollTo(sub.id)}
                          style={{
                            ...subBtn,
                            ...(activeAnchor === sub.id ? subBtnActive : null),
                          }}
                        >
                          {sub.title}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </aside>

        <section style={contentCard}>
          <div id="aa-scroll" style={contentScroll}>
            <section style={hero}>
              <div style={heroLabel}>{t("help.account.ui.guide", "Guide")}</div>
              <h1 style={h1}>{t("help.account.pageTitle", "Account Administration")}</h1>
              <p style={subhead}>
                {t(
                  "help.account.pageSubtitle",
                  "Find help for account access, settings, privacy, security, and support."
                )}
              </p>
            </section>

            {groups.map((group) => (
              <section key={group.id} style={groupSection}>
                <div style={groupIntro}>
                  <h2 style={groupTitle}>{group.title}</h2>
                  {group.description && <p style={groupDescription}>{group.description}</p>}
                </div>

                <div style={topicGrid}>
                  {group.subtopics.map((subtopic) => (
                    <article key={subtopic.id} id={subtopic.id} style={topicCard}>
                      <div style={topicTitleRow}>
                        <h3 style={topicTitle}>{subtopic.title}</h3>

                        <button
                          type="button"
                          onClick={() => {
                            const url = `${window.location.origin}${window.location.pathname}#${subtopic.id}`;
                            navigator.clipboard?.writeText(url).catch(() => {});
                          }}
                          style={copyBtn}
                          title={t("help.account.ui.copyLink", "Copy link")}
                          aria-label={t("help.account.ui.copyLink", "Copy link")}
                        >
                          ⧉
                        </button>
                      </div>

                      <ul style={topicList}>
                        {subtopic.body.map((item, idx) => (
                          <li key={idx} style={topicItem}>
                            <span style={dot}>•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </article>
                  ))}
                </div>
              </section>
            ))}
          </div>
        </section>
      </main>

      <style>{`
        button {
          font-family: inherit;
        }

        button:hover {
          opacity: 0.92;
        }

        button:focus-visible {
          outline: 2px solid rgba(163,230,53,0.65);
          outline-offset: 3px;
        }

        #aa-scroll::-webkit-scrollbar,
        nav::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }

        #aa-scroll::-webkit-scrollbar-thumb,
        nav::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,0.25);
          border-radius: 999px;
        }

        @media (max-width: 900px) {
          .aa-main-mobile {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

export default AccountAdministration;

const shell: React.CSSProperties = {
  height: "100vh",
  width: "100%",
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
  background: "radial-gradient(circle at top, #020617 0, #000 42%, #020617 100%)",
  color: "#b0b8c0",
  fontFamily: 'system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
};

const header: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 30,
  height: 62,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "8px 25px",
  background: "rgba(2, 6, 23, 0.92)",
  backdropFilter: "blur(10px)",
  borderBottom: "1px solid rgba(148,163,184,0.18)",
  boxSizing: "border-box",
};

const headerLeft: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
};

const headerTitle: React.CSSProperties = {
  fontSize: 15,
  fontWeight: 900,
  color: "#b0b8c0",
};

const main: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: "grid",
  gridTemplateColumns: "320px minmax(0, 1fr)",
  gap: 18,
  padding: "18px 3vw",
  overflow: "hidden",
  boxSizing: "border-box",
};

const sidebar: React.CSSProperties = {
  minHeight: 0,
  borderRadius: 24,
  border: "1px solid rgba(148,163,184,0.2)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.92), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.72)",
  padding: 16,
  overflow: "hidden",
  display: "flex",
  flexDirection: "column",
};

const sidebarHeader: React.CSSProperties = {
  padding: "8px 8px 14px",
  borderBottom: "1px solid rgba(148,163,184,0.14)",
};

const sidebarEyebrow: React.CSSProperties = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.12,
  color: "#84cc16",
  fontWeight: 900,
};

const sidebarTitle: React.CSSProperties = {
  margin: "8px 0 0",
  fontSize: 22,
  lineHeight: 1.1,
  color: "#e5e7eb",
  letterSpacing: -0.04,
};

const sidebarText: React.CSSProperties = {
  margin: "10px 0 0",
  fontSize: 13,
  lineHeight: 1.45,
  color: "#8d9298",
};

const nav: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  padding: "14px 4px 4px",
};

const navGroup: React.CSSProperties = {
  marginBottom: 8,
};

const groupBtn: React.CSSProperties = {
  width: "100%",
  border: "1px solid rgba(148,163,184,0.16)",
  background: "rgba(255,255,255,0.035)",
  color: "#b0b8c0",
  borderRadius: 16,
  padding: "12px 12px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  fontSize: 14,
  fontWeight: 900,
  textAlign: "left",
};

const groupBtnActive: React.CSSProperties = {
  border: "1px solid rgba(163,230,53,0.35)",
  background: "rgba(163,230,53,0.09)",
  color: "#e5e7eb",
};

const chevron: React.CSSProperties = {
  width: 22,
  height: 22,
  borderRadius: 999,
  display: "grid",
  placeItems: "center",
  background: "rgba(255,255,255,0.06)",
  color: "#9ca3af",
  flex: "0 0 auto",
};

const subNav: React.CSSProperties = {
  marginTop: 7,
  marginLeft: 8,
  paddingLeft: 10,
  borderLeft: "1px solid rgba(148,163,184,0.18)",
  display: "grid",
  gap: 6,
};

const subBtn: React.CSSProperties = {
  width: "100%",
  border: "none",
  background: "transparent",
  color: "#8d9298",
  borderRadius: 12,
  padding: "9px 10px",
  cursor: "pointer",
  fontSize: 13,
  fontWeight: 750,
  textAlign: "left",
};

const subBtnActive: React.CSSProperties = {
  background: "rgba(255,255,255,0.07)",
  color: "#e5e7eb",
};

const contentCard: React.CSSProperties = {
  minHeight: 0,
  borderRadius: 24,
  border: "1px solid rgba(148,163,184,0.2)",
  background: "linear-gradient(145deg, rgba(15,23,42,0.9), rgba(3,7,18,1))",
  boxShadow: "0 22px 55px rgba(0,0,0,0.8)",
  overflow: "hidden",
};

const contentScroll: React.CSSProperties = {
  height: "100%",
  overflowY: "auto",
  padding: "22px 22px 60px",
  boxSizing: "border-box",
};

const hero: React.CSSProperties = {
  borderRadius: 22,
  border: "1px solid rgba(148,163,184,0.16)",
  background:
    "linear-gradient(135deg, rgba(163,230,53,0.09), rgba(255,255,255,0.035) 45%, rgba(2,6,23,0.4))",
  padding: "24px 24px",
  marginBottom: 18,
};

const heroLabel: React.CSSProperties = {
  color: "#84cc16",
  fontSize: 12,
  fontWeight: 950,
  textTransform: "uppercase",
  letterSpacing: 0.12,
};

const h1: React.CSSProperties = {
  margin: "8px 0 0",
  fontSize: 32,
  lineHeight: 1.05,
  color: "#e5e7eb",
  letterSpacing: -0.05,
};

const subhead: React.CSSProperties = {
  margin: "12px 0 0",
  color: "#9ca3af",
  fontSize: 15,
  lineHeight: 1.6,
  maxWidth: 900,
};

const groupSection: React.CSSProperties = {
  scrollMarginTop: 20,
  marginTop: 24,
};

const groupIntro: React.CSSProperties = {
  marginBottom: 12,
};

const groupTitle: React.CSSProperties = {
  margin: 0,
  color: "#e5e7eb",
  fontSize: 22,
  letterSpacing: -0.035,
};

const groupDescription: React.CSSProperties = {
  margin: "8px 0 0",
  color: "#8d9298",
  fontSize: 14,
  lineHeight: 1.5,
};

const topicGrid: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 12,
};

const topicCard: React.CSSProperties = {
  scrollMarginTop: 18,
  borderRadius: 20,
  border: "1px solid rgba(148,163,184,0.16)",
  background: "rgba(255,255,255,0.04)",
  padding: 16,
  boxShadow: "0 12px 34px rgba(0,0,0,0.42)",
};

const topicTitleRow: React.CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: 10,
};

const topicTitle: React.CSSProperties = {
  margin: 0,
  color: "#e5e7eb",
  fontSize: 17,
  lineHeight: 1.25,
  letterSpacing: -0.025,
};

const copyBtn: React.CSSProperties = {
  flex: "0 0 auto",
  width: 34,
  height: 34,
  borderRadius: 12,
  border: "1px solid rgba(148,163,184,0.22)",
  background: "rgba(2, 6, 23, 0.35)",
  color: "#b1b8c7",
  cursor: "pointer",
};

const topicList: React.CSSProperties = {
  margin: "13px 0 0",
  padding: 0,
  listStyle: "none",
  display: "grid",
  gap: 9,
};

const topicItem: React.CSSProperties = {
  display: "flex",
  gap: 8,
  alignItems: "flex-start",
  color: "#b0b8c0",
  fontSize: 14,
  lineHeight: 1.55,
};

const dot: React.CSSProperties = {
  color: "rgba(163,230,53,0.75)",
  flex: "0 0 auto",
  marginTop: 3,
};